import { fetchAuthSession } from 'aws-amplify/auth';

// when doing full-stack dev on a branch, you may have deployed a separate gateway, which you can supply as an env var for use here
export const API_URL = import.meta.env.VITE_API_URL || `https://f65x8fymgh.execute-api.eu-west-2.amazonaws.com/prod`;

export type RecordKind = 'person' | 'business' | 'location' | null;

export type Gender = 'female' | 'male' | 'other';

export const getRecords = async (selectedDateRange: string): Promise<any[]> => {

    let cognitoToken = null;
    const session = await fetchAuthSession();
    if (session.tokens && session.tokens.idToken) {
        cognitoToken = session.tokens.idToken.toString();
    } else {
        throw new Error("No valid Cognito token found.");
    }

    if (!cognitoToken) {
        throw new Error("No Cognito token provided.");
    }

    let allRecords: any[] = []; // To store all fetched records
    let lastEvaluatedKey = null; // Pagination key

    // Calculate days_back based on the selectedDateRange
    let daysBack: number | null = null;
    switch (selectedDateRange) {
        case 'lastweek':
            daysBack = 7;
            break;
        case 'lastmonth':
            daysBack = 30;
            break;
        case 'last3months':
            daysBack = 90;
            break;
        case 'last6months':
            daysBack = 180;
            break;
        case 'last12months':
            daysBack = 365;
            break;
        case 'last18months':
            daysBack = 550;
            break;
        case 'last24months':
            daysBack = 730;
            break;
        default:
            daysBack = null; // No limit for 'All'
    }

    // Construct the API URL with query parameters for pagination
    do {
        const url = new URL(`${API_URL}/records`);
        if (daysBack !== null) {
            url.searchParams.append("days_back", daysBack.toString());
        }
        if (lastEvaluatedKey) {
            url.searchParams.append("start_key", JSON.stringify(lastEvaluatedKey));
        }

        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${cognitoToken}`, // Pass the Cognito token in the Authorization header
            },
        });

        if (!response.ok) {
            throw new Error(`Error fetching data: ${response.statusText}`);
        }

        const responseData = await response.json();

        // Append the new records to the existing list
        allRecords = allRecords.concat(responseData.records);

        // Update the lastEvaluatedKey for the next iteration
        lastEvaluatedKey = responseData.LastEvaluatedKey || null;
    } while (lastEvaluatedKey); // Continue while there are more pages to fetch

    return allRecords;
};


export interface AreaData {
    ID: string;
    Name: string;
    Geometry: any
}


let allAreas: AreaData[] = []; 

export const getAreas = async (refresh: boolean=false): Promise<AreaData[]> => {

    if (!refresh && allAreas.length){
        return allAreas;
    }

    let cognitoToken = null;
    const session = await fetchAuthSession();
    if (session.tokens && session.tokens.idToken) {
        cognitoToken = session.tokens.idToken.toString();
    } else {
        throw new Error("No valid Cognito token found.");
    }

    if (!cognitoToken) {
        throw new Error("No Cognito token provided.");
    }

    let lastEvaluatedKey = null; // Pagination key

    let partialResult : any[] = [];

    // Construct the API URL with query parameters for pagination
    do {
        const url = new URL(`${API_URL}/areas`);
        if (lastEvaluatedKey) {
            url.searchParams.append("start_key", JSON.stringify(lastEvaluatedKey));
        }

        const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
                Authorization: `Bearer ${cognitoToken}`, // Pass the Cognito token in the Authorization header
            },
        });

        if (!response.ok) {
            throw new Error(`Error fetching data: ${response.statusText}`);
        }

        const responseData = await response.json();

        // Append the new records to the existing list
        partialResult = partialResult.concat(responseData.areas);

        // Update the lastEvaluatedKey for the next iteration
        lastEvaluatedKey = responseData.LastEvaluatedKey || null;
    } while (lastEvaluatedKey); // Continue while there are more pages to fetch

    allAreas = partialResult.map((record: any) => ({
        ID: record.id,
        Name: record.name,
        Geometry: record.geometry 
    }));

    return allAreas;
};


export interface ReportingCreds {
    guest_token: string;
    dashboard_ids: string[]
};

export const getReportingCreds = async (): Promise<ReportingCreds> => {
    let cognitoToken = null;
    const session = await fetchAuthSession();
    if (session.tokens && session.tokens.idToken) {
        cognitoToken = session.tokens.idToken.toString();
    } else {
        throw new Error("No valid Cognito token found.");
    }

    if (!cognitoToken) {
        throw new Error("No Cognito token provided.");
    }


    const response = await fetch(`${API_URL}/reporting/creds`, {
        method: "GET",
        headers: { Authorization: `Bearer ${cognitoToken}` },
    });

    if (!response.ok) {
        throw new Error(`Error fetching data: ${response.statusText}`);
    }

    return await response.json();
};
